import { useCallback, useMemo } from 'react';
import type { ListActivePaymentDto, PaymentPollingDto } from '../../generated-api';
import { PaymentState } from '../../generated-api';
import { usePollPaymentStatusList } from './usePaymentPollingApi';
import { isDefined } from '../utils/typeguards';
import { useToast } from '@ordr-as/umami-react/toast';
import PaymentToastContent, { COUNTDOWN_DURATION_MS } from '../components/PaymentToastContent';
import useIntl from './useIntl';
import { useLocation } from '../context/LocationContext';
import { CashDrawerStatus, useCashStatus } from './useCashStatus';
import useDeviceId from './useDeviceId';
import { usePrintReceiptContext } from '../context/PrintReceiptContextProvider';
import { useScreenSize } from '../context/ScreenSizeContext';
import { usePinScreenSettings } from '../context/PinScreenSettingsContext';
import { CashPaymentStatus, useCashPaymentStatus } from './useCashPaymentStatus';
import { useHub } from '../context/HubProvider';
import { UUID_REGEX } from '../constants/payment';
import { useTerminalStore } from '../context/TerminalStore';
import { useFocusedPaymentStore } from '../context/FocusedPaymentStore';
import { useActivePaymentStore } from '../context/ActivePaymentStore';
import { clearPaymentOrigin, getDigitalBillReference } from '../utils/paymentOriginTracker';
import {
  getGetBookingDetailDtoQueryKey,
  getGetSettledBookingsV2QueryKey,
  getGetUnsettledBookingsV2QueryKey,
} from '@ordr-as/ordr-service-types/epos';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  onPaymentCaptured?: (payment: ListActivePaymentDto) => void;
  onPaymentFailed?: (payment: ListActivePaymentDto) => void;
}

export const useActivePayments = ({ onPaymentCaptured, onPaymentFailed }: Props = {}) => {
  const { location, featureFlags } = useLocation();
  const intl = useIntl();
  const activePayments = useActivePaymentStore((s) => s.payments);
  const hub = useHub();
  const queryClient = useQueryClient();
  const toasts = useToast();
  const cashStatus = useCashStatus();
  const deviceId = useDeviceId();
  const { isPhone } = useScreenSize();
  const { handlePrintReceipt } = usePrintReceiptContext();
  const { initiateChangeUserOnCompletedSale } = usePinScreenSettings();
  const { setCashPaymentStatus } = useCashPaymentStatus();

  const isFailedPaymentPromptEnabled = featureFlags.failedPaymentPromptFeatureFlag?.enabled;

  const addActivePayment = useCallback(
    (nextPayment: ListActivePaymentDto) => {
      if (nextPayment.paymentMethod.provider !== 'UNINTEGRATED') {
        hub.broadcast('payment.started', { locationId: location.locationId });
      }
      useActivePaymentStore.getState().actions.addPayment(nextPayment);
    },
    [hub, location.locationId]
  );

  const removeActivePayment = useCallback(
    (payment: ListActivePaymentDto) => {
      useActivePaymentStore.getState().actions.removePayment(payment);
      if (payment.paymentReference) {
        useTerminalStore.getState().actions.clearPayment(payment.paymentReference);
      }
      if (payment.paymentMethod.provider !== 'UNINTEGRATED') {
        const isPending = !payment.paymentReference || UUID_REGEX.test(payment.paymentReference);
        hub.broadcast('payment.finished', { locationId: location.locationId });
        if (!isPending) {
          hub.broadcast('payment.finished.v2', {
            locationId: location.locationId,
            paymentReference: payment.paymentReference,
          });
        }
      }
    },
    [hub, location.locationId]
  );

  const printReceipt = useCallback(
    (payment: ListActivePaymentDto) => {
      handlePrintReceipt(payment.paymentReference || '', payment.paymentMethod.id);
      toasts.remove(payment.paymentReference || '');
    },
    [handlePrintReceipt, toasts]
  );

  const handlePaymentCaptured = useCallback(
    (payment: ListActivePaymentDto) => {
      if (!payment.paymentReference) {
        return;
      }

      removeActivePayment(payment);

      const digitalBillRef = getDigitalBillReference(payment.paymentReference);
      if (digitalBillRef) {
        const { locationId } = location;
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: getGetBookingDetailDtoQueryKey(locationId, digitalBillRef),
          });
          queryClient.invalidateQueries({ queryKey: getGetUnsettledBookingsV2QueryKey(locationId) });
          queryClient.invalidateQueries({ queryKey: getGetSettledBookingsV2QueryKey(locationId) });
        }, 1500);
        clearPaymentOrigin(payment.paymentReference);
      }

      const { focusedPayment, setFocusedPayment } = useFocusedPaymentStore.getState();
      if (payment.paymentReference === focusedPayment?.paymentReference) {
        setFocusedPayment(undefined);
      }

      if (
        (cashStatus !== CashDrawerStatus.CONNECTED || payment.paymentMethod.paymentMethod !== 'CASH') &&
        payment.deviceId === deviceId
      ) {
        toasts.pushSuccess({
          direction: isPhone ? 'top' : 'bottom',
          duration: COUNTDOWN_DURATION_MS,
          id: payment.paymentReference,
          message: (
            <PaymentToastContent
              payment={payment}
              showCountdown
              swipeUp={isPhone}
              onDismiss={() => toasts.remove(payment.paymentReference || '')}
            />
          ),
          toastAction: {
            actionLabel: 'Receipt',
            onAction: () => printReceipt(payment),
          },
        });
      }

      if (
        cashStatus === CashDrawerStatus.CONNECTED &&
        payment.paymentMethod.paymentMethod === 'CASH' &&
        payment.deviceId === deviceId
      ) {
        setCashPaymentStatus(CashPaymentStatus.INITIATED);
      }

      setTimeout(() => {
        toasts.remove(payment.paymentReference || '');
        initiateChangeUserOnCompletedSale(payment.paymentMethod);
      }, COUNTDOWN_DURATION_MS);

      onPaymentCaptured?.(payment);
    },
    [
      cashStatus,
      deviceId,
      initiateChangeUserOnCompletedSale,
      isPhone,
      location.locationId,
      onPaymentCaptured,
      printReceipt,
      queryClient,
      removeActivePayment,
      setCashPaymentStatus,
      toasts,
    ]
  );

  const handlePaymentFailed = useCallback(
    (payment: ListActivePaymentDto) => {
      if (!payment.paymentReference) {
        return;
      }

      removeActivePayment(payment);

      const { focusedPayment, setFocusedPayment } = useFocusedPaymentStore.getState();
      if (payment.paymentReference === focusedPayment?.paymentReference) {
        setFocusedPayment(undefined);
      }

      if (payment.deviceId === deviceId && !isFailedPaymentPromptEnabled) {
        toasts.pushError({
          direction: isPhone ? 'top' : 'bottom',
          id: payment.paymentReference,
          message: <PaymentToastContent payment={payment} />,
          toastAction: {
            actionLabel: intl.formatMessage({ id: 'generic.fix' }),
            onAction: () => {
              useFocusedPaymentStore.getState().setFocusedPayment(payment);
              toasts.remove(payment.paymentReference || '');
            },
          },
        });
      }

      if (isFailedPaymentPromptEnabled) {
        setFocusedPayment(payment);
      }

      onPaymentFailed?.(payment);
    },
    [deviceId, intl, isPhone, onPaymentFailed, removeActivePayment, toasts, isFailedPaymentPromptEnabled]
  );

  const handlePaidOrFailedPayment = useCallback(
    (payment: ListActivePaymentDto) => {
      if (payment.paymentState === PaymentState.SUCCEEDED) {
        handlePaymentCaptured(payment);
      } else if (payment.paymentState === PaymentState.FAILED) {
        handlePaymentFailed(payment);
      }
    },
    [handlePaymentCaptured, handlePaymentFailed]
  );

  const handlePollingSuccess = useCallback(
    (data: PaymentPollingDto[]) => {
      data.forEach((pollingResult) => {
        // Read fresh from store to avoid stale closure
        const { payments } = useActivePaymentStore.getState();
        const payment = payments.find((p) => p.paymentReference === pollingResult.paymentReference);
        if (!payment) {
          return;
        }
        const paymentToProcess: ListActivePaymentDto = { ...payment, paymentState: pollingResult.paymentState };
        handlePaidOrFailedPayment(paymentToProcess);
      });
    },
    [handlePaidOrFailedPayment]
  );

  const inProgressPayments = useMemo(
    () =>
      activePayments.filter(
        ({ paymentState }) => paymentState !== PaymentState.SUCCEEDED && paymentState !== PaymentState.FAILED
      ),
    [activePayments]
  );

  // Pending payments use a BFF-generated correlationId (UUID) as their reference,
  // Which payment-service doesn't know about. All other payments use a non-UUID
  // Payment reference that payment-service can resolve.
  const bffPaymentReferences = useMemo(
    () =>
      inProgressPayments
        .map((ap) => ap.paymentReference)
        .filter(isDefined)
        .filter((ref) => UUID_REGEX.test(ref)),
    [inProgressPayments]
  );

  usePollPaymentStatusList(bffPaymentReferences, bffPaymentReferences.length > 0, handlePollingSuccess);

  return { activePayments, addActivePayment, handlePaidOrFailedPayment, removeActivePayment };
};
